import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../../services/api';
import ToastService from '../../core/notifications/ToastService';

interface Slot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  totalCapacity: number;
  bookedCount: number;
  availableCapacity: number;
  occupancyPercentage: number;
  priceCoins: number;
  priceCash: number;
  isActive: boolean;
  description?: string;
}

export const SlotsListPage: React.FC = () => {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const fetchSlots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { page, limit };
      if (dateFilter) params.date = dateFilter;

      const response = await apiClient.get<{ data: { slots: Slot[] } }>(`/slots/gyms/${gymId}/slots`, { params });
      setSlots((response as any).data?.slots || []);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to fetch slots';
      setError(message);
      ToastService.error(message);
    } finally {
      setLoading(false);
    }
  }, [gymId, dateFilter, page, limit]);

  useEffect(() => {
    if (gymId) {
      fetchSlots();
    }
  }, [gymId, fetchSlots]);

  const handleDeleteSlot = async () => {
    if (!selectedSlot) return;
    try {
      await apiClient.delete(`/slots/gyms/${gymId}/slots/${selectedSlot.id}`);
      ToastService.success('Slot deleted successfully');
      setShowDeleteDialog(false);
      fetchSlots();
    } catch (err: any) {
      ToastService.error(err.response?.data?.message || 'Failed to delete slot');
    }
  };

  const getOccupancyColor = (percentage: number) => {
    if (percentage >= 90) return '#EF4444';
    if (percentage >= 70) return '#F59E0B';
    return '#10B981';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Slots Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage time slots and pricing for your gym
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/pricing/gyms/${gymId}/slots/create`)}
          >
            Create Slot
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/pricing/gyms/${gymId}/slots/bulk`)}
            sx={{ backgroundColor: '#14B8A6' }}
          >
            Bulk Create
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Filter by Date"
                value={dateFilter}
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(1);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setDateFilter('');
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Slots Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : slots.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              No slots found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/pricing/gyms/${gymId}/slots/create`)}
              sx={{ backgroundColor: '#14B8A6' }}
            >
              Create Your First Slot
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Capacity</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Booked</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Occupancy</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {slots.map((slot) => (
                <TableRow key={slot.id} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                  <TableCell>{slot.date}</TableCell>
                  <TableCell>{`${slot.startTime} - ${slot.endTime}`}</TableCell>
                  <TableCell align="right">{slot.totalCapacity}</TableCell>
                  <TableCell align="right">{slot.bookedCount}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${slot.occupancyPercentage}%`}
                      size="small"
                      sx={{
                        backgroundColor: getOccupancyColor(slot.occupancyPercentage) + '20',
                        color: getOccupancyColor(slot.occupancyPercentage),
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#14B8A6' }}>
                      ₹{slot.priceCash}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={slot.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        backgroundColor: slot.isActive ? '#10B98120' : '#EF444420',
                        color: slot.isActive ? '#10B981' : '#EF4444'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => navigate(`/pricing/gyms/${gymId}/slots/${slot.id}/edit`)}
                      >
                        <EditIcon fontSize="small" />
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setSelectedSlot(slot);
                          setShowDeleteDialog(true);
                        }}
                        sx={{ color: '#EF4444' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Slot</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete this slot? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteSlot}
            variant="contained"
            sx={{ backgroundColor: '#EF4444' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SlotsListPage;
